import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, X, Upload, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import Header from 'components/ui/Header';
import Footer from 'pages/landing-page/components/Footer';
import { supabase } from 'lib/supabase';

// ─── Star Selector ────────────────────────────────────────────────────────────
const StarSelector = ({ value, onChange, size = 'large', label }) => {
  const [hovered, setHovered] = useState(0);
  const starSize = size === 'large' ? 40 : 28;

  return (
    <div>
      {label && (
        <p className="text-sm font-medium text-gray-600 mb-2">{label}</p>
      )}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
            aria-label={`${star} stelle`}
          >
            <Star
              size={starSize}
              fill={(hovered || value) >= star ? '#F59E0B' : 'none'}
              stroke={(hovered || value) >= star ? '#F59E0B' : '#D1D5DB'}
              strokeWidth={1.5}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ReviewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  // Token validation state
  const [status, setStatus] = useState('loading'); // loading | valid | invalid | used | expired
  const [invitation, setInvitation] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [rating, setRating] = useState(0);
  const [ratingPunctuality, setRatingPunctuality] = useState(0);
  const [ratingQuality, setRatingQuality] = useState(0);
  const [ratingPrice, setRatingPrice] = useState(0);
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState([]); // { file, preview, base64 }
  const [photoError, setPhotoError] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // ── Token validation on mount ──────────────────────────────────────────────
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setStatus('invalid');
        setErrorMessage('Link non valido o scaduto');
        return;
      }

      try {
        const { data, error } = await supabase?.from('review_invitations')?.select(`
            *,
            professional:users!review_invitations_professional_id_fkey(full_name, profession)
          `)?.eq('token', token)?.limit(1)?.maybeSingle();

        if (error) {
          console.error('Token validation error:', error);
          // Try alternative join approach
          const { data: rawData, error: rawError } = await supabase?.rpc('get_review_invitation_by_token', { p_token: token })?.maybeSingle();

          if (rawError || !rawData) {
            setStatus('invalid');
            setErrorMessage('Link non valido o scaduto');
            return;
          }
          processInvitation(rawData);
          return;
        }

        if (!data) {
          setStatus('invalid');
          setErrorMessage('Link non valido o scaduto');
          return;
        }

        processInvitation(data);
      } catch (err) {
        console.error('Unexpected error during token validation:', err);
        setStatus('invalid');
        setErrorMessage('Link non valido o scaduto');
      }
    };

    validateToken();
  }, [token]);

  const processInvitation = (data) => {
    if (data?.used_at) {
      setStatus('used');
      return;
    }

    if (data?.expires_at && new Date(data.expires_at) < new Date()) {
      setStatus('expired');
      return;
    }

    setInvitation(data);
    setStatus('valid');
  };

  // ── Photo handling ─────────────────────────────────────────────────────────
  const handlePhotoAdd = useCallback(async (e) => {
    setPhotoError('');
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;

    const remaining = 10 - photos?.length;
    if (remaining <= 0) {
      setPhotoError('Hai già caricato il massimo di 10 foto.');
      e.target.value = '';
      return;
    }

    const toProcess = files?.slice(0, remaining);
    const newPhotos = [];

    for (const file of toProcess) {
      if (file?.size > 5 * 1024 * 1024) {
        setPhotoError(`"${file?.name}" supera i 5 MB. File ignorato.`);
        continue;
      }
      const base64 = await fileToBase64(file);
      const preview = URL.createObjectURL(file);
      newPhotos?.push({ file, preview, base64: base64?.base64, contentType: base64?.contentType });
    }

    setPhotos((prev) => [...prev, ...newPhotos]);
    e.target.value = '';
  }, [photos]);

  const removePhoto = useCallback((index) => {
    setPhotos((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated?.[index]?.preview);
      updated?.splice(index, 1);
      return updated;
    });
  }, []);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        base64: reader.result,
        contentType: file.type
      });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (rating === 0) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const body = {
        token,
        rating,
        rating_punctuality: ratingPunctuality || null,
        rating_quality: ratingQuality || null,
        rating_price: ratingPrice || null,
        text: text?.trim() || null,
        photos: photos?.map((p) => ({ base64: p?.base64, contentType: p?.contentType })),
      };

      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/submit-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : { Authorization: `Bearer ${supabaseAnonKey}` }),
        },
        body: JSON.stringify(body),
      });

      if (!response?.ok) {
        const errData = await response?.json()?.catch(() => ({}));
        throw new Error(errData?.message || `Errore ${response.status}`);
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(err?.message || 'Si è verificato un errore. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const professionalName =
    invitation?.professional?.full_name ||
    invitation?.professional_name ||
    'il professionista';

  const profession =
    invitation?.professional?.profession ||
    invitation?.profession ||
    '';

  // ── Loading ────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main style={{ paddingTop: 'var(--header-height)' }} className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
            <p className="text-gray-500 text-sm">Verifica del link in corso…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Error states ───────────────────────────────────────────────────────────
  if (status === 'invalid' || status === 'used' || status === 'expired') {
    const config = {
      invalid: {
        icon: <AlertCircle size={48} className="text-red-400" />,
        title: 'Link non valido o scaduto',
        desc: 'Il link che hai utilizzato non è valido o è già scaduto.',
        color: 'red',
      },
      used: {
        icon: <CheckCircle size={48} className="text-green-400" />,
        title: 'Hai già lasciato la tua recensione. Grazie!',
        desc: 'La tua recensione è già stata registrata con successo.',
        color: 'green',
      },
      expired: {
        icon: <Clock size={48} className="text-amber-400" />,
        title: 'Link scaduto',
        desc: 'Il link per lasciare la recensione è scaduto.',
        color: 'amber',
      },
    }?.[status];

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main style={{ paddingTop: 'var(--header-height)' }} className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
            <div className="flex justify-center mb-5">{config?.icon}</div>
            <h1 className="text-xl font-bold text-gray-900 mb-3">{config?.title}</h1>
            <p className="text-gray-500 text-sm mb-8">{config?.desc}</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#104d98' }}
            >
              Torna alla home
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Success state ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main style={{ paddingTop: 'var(--header-height)' }} className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
            <div className="flex justify-center mb-5">
              <CheckCircle size={56} className="text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              Grazie! La tua recensione è stata pubblicata.
            </h1>
            <p className="text-gray-500 text-sm mb-8">
              La tua opinione aiuterà altri clienti a scegliere il professionista giusto.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#104d98' }}
            >
              Torna alla home
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main style={{ paddingTop: 'var(--header-height)' }} className="py-10 px-4">
        <div className="max-w-xl mx-auto">

          {/* Header section */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              Recensisci {professionalName}
            </h1>
            {profession && (
              <p className="text-base font-medium mb-2" style={{ color: '#2780b5' }}>
                {profession}
              </p>
            )}
            <p className="text-sm text-gray-500 italic">
              La tua opinione aiuta altri clienti a scegliere
            </p>
          </div>

          {/* Form card */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 flex flex-col gap-8">

            {/* 1. Overall rating */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-3">
                Valutazione complessiva <span className="text-red-500">*</span>
              </label>
              <StarSelector
                value={rating}
                onChange={setRating}
                size="large"
                label="Valutazione complessiva"
              />
              {rating === 0 && (
                <p className="text-xs text-gray-400 mt-2">
                  Seleziona almeno una stella per procedere
                </p>
              )}
            </div>

            {/* 2. Sub-ratings */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-4">
                Valutazioni dettagliate <span className="text-xs font-normal text-gray-400">(facoltativo)</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-5 sm:gap-6">
                <StarSelector
                  value={ratingPunctuality}
                  onChange={setRatingPunctuality}
                  size="small"
                  label="Puntualità"
                />
                <StarSelector
                  value={ratingQuality}
                  onChange={setRatingQuality}
                  size="small"
                  label="Qualità del lavoro"
                />
                <StarSelector
                  value={ratingPrice}
                  onChange={setRatingPrice}
                  size="small"
                  label="Rapporto qualità/prezzo"
                />
              </div>
            </div>

            {/* 3. Text */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-2">
                La tua esperienza <span className="text-xs font-normal text-gray-400">(facoltativo)</span>
              </label>
              <div className="relative">
                <textarea
                  value={text}
                  onChange={(e) => setText(e?.target?.value?.slice(0, 1000))}
                  placeholder="Racconta la tua esperienza (facoltativo)"
                  rows={5}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:border-transparent transition"
                  style={{ '--tw-ring-color': '#32b3e5' }}
                />
                <span className="absolute bottom-3 right-3 text-xs text-gray-400">
                  {text?.length}/1000
                </span>
              </div>
            </div>

            {/* 4. Photos */}
            <div>
              <label className="block text-base font-semibold text-gray-900 mb-3">
                Foto del lavoro <span className="text-xs font-normal text-gray-400">(facoltativo, max 10)</span>
              </label>

              {/* Privacy disclaimer */}
              <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                <span className="text-base leading-none mt-0.5">⚠️</span>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Non includere persone identificabili, targhe, documenti o dati personali.
                  Carica solo foto del lavoro svolto.
                </p>
              </div>

              {/* Thumbnails */}
              {photos?.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-4">
                  {photos?.map((photo, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 group">
                      <img
                        src={photo?.preview}
                        alt={`Foto ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Rimuovi foto"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Counter */}
              <p className="text-xs text-gray-500 mb-3">
                {photos?.length}/10 foto caricate
              </p>

              {/* File input */}
              {photos?.length < 10 && (
                <label className="flex items-center gap-3 cursor-pointer border-2 border-dashed border-gray-200 rounded-xl px-4 py-4 hover:border-blue-300 transition-colors">
                  <Upload size={18} className="text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-500">
                    Aggiungi foto (JPEG, PNG, WebP — max 5 MB ciascuna)
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoAdd}
                    className="hidden"
                  />
                </label>
              )}

              {photoError && (
                <p className="text-xs text-red-500 mt-2">{photoError}</p>
              )}
            </div>

            {/* Submit error */}
            {submitError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="w-full py-4 rounded-xl text-white text-base font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: submitting || rating === 0 ? '#9CA3AF' : '#104d98' }}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Invio in corso…
                </span>
              ) : (
                'Invia recensione'
              )}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReviewPage;
